// src/components/order/hooks.ts
import { useCallback, useState } from "react";
import useSWR from "swr";
import { BranchApi, NetworkApi, StaffApi } from "../../apis";
import * as GovernanceApi from "../../apis/governance-config";
import type { IAvailableDeliveryDatesResponse } from "../../apis/governance-config";
import { message } from "../../utils/message";
import HttpHandler from "../../utils/http-handler";

/**
 * Calculates 6 future delivery dates for order scheduling.
 *
 * The calculation works as follows:
 * - Takes the current timestamp and calculates a base point aligned to a 3-day interval
 * - The modulo operation (60 * 60 * 24 * 3 * 1000 = 259200000ms = 3 days) aligns to a reference point
 * - Each offset (176400000ms, 262800000ms, etc.) adds approximately 2 days, 3 days, 4 days, etc.
 *   from the base point, creating 6 sequential delivery date options
 * - Returns objects with id, date, and weekend (day of week: 0=Sunday, 6=Saturday)
 */
export const useDeliveryDays = () => {
  const dates = new Date();
  const ts = +dates;
  // 3-day interval in milliseconds: 60 * 60 * 24 * 3 * 1000 = 259200000ms
  const intervalMs = 60 * 60 * 24 * (7 - 4) * 1000;
  const baseTime = ts - (ts % intervalMs);

  // Offsets represent days from base: ~2d, ~3d, ~4d, ~5d, ~6d, ~7d ahead
  const monday1 = baseTime + 176400000;
  const monday2 = baseTime + 262800000;
  const monday3 = baseTime + 349200000;
  const monday4 = baseTime + 435600000;
  const monday5 = baseTime + 522000000;
  const monday6 = baseTime + 608400000;

  return [
    { id: "1", date: new Date(monday1), weekend: new Date(monday1).getDay() },
    { id: "2", date: new Date(monday2), weekend: new Date(monday2).getDay() },
    { id: "3", date: new Date(monday3), weekend: new Date(monday3).getDay() },
    { id: "4", date: new Date(monday4), weekend: new Date(monday4).getDay() },
    { id: "5", date: new Date(monday5), weekend: new Date(monday5).getDay() },
    { id: "6", date: new Date(monday6), weekend: new Date(monday6).getDay() },
  ];
};

// Network data hook
export const useNetworkData = (hasAccess: boolean) => {
  const fetcher = useCallback(async () => {
    const res = await NetworkApi.select({ regNumber: "", partnerName: "", limit: 1000 });
    const mappedRes = res.reduce((acc: any, it: any) => {
      acc[it.id] = it;
      return acc;
    }, {});
    return { res, mappedRes };
  }, []);

  return useSWR(
    hasAccess ? `/ord/network/select/partner-data` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
};

// Business data hook
export const useBusinessData = (initial: any = null) => {
  const [businessData, setBusinessData] = useState<any>(initial);

  const fetchBusiness = async (id: string, setFieldValue?: any) => {
    if (id) {
      try {
        const res = await NetworkApi.get(id);
        setBusinessData(res);
        if (setFieldValue) {
          setFieldValue("lines", undefined);
        }
        return res;
      } catch (error) {
        message.error((error as HttpHandler)?.message!);
      }
    }
  };

  return { businessData, setBusinessData, fetchBusiness };
};

// Staff data hook (buyer side only)
export const useStaffData = (businessId: string | undefined, hasAccess: boolean) => {
  const fetcher = useCallback(async () => {
    const res = await StaffApi.select({ businessId: businessId! });
    return res;
  }, [businessId]);

  return useSWR(
    businessId && hasAccess ? `/ord/staff/select/${businessId}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
};

// Branch data hook (buyer side only)
export const useBranchData = (businessId: string | undefined, hasAccess: boolean) => {
  const fetcher = useCallback(async () => {
    const res = await BranchApi.select({ businessId: businessId! });
    return res;
  }, [businessId]);

  return useSWR(
    businessId && hasAccess ? `/ord/branch/select/${businessId}` : null,
    fetcher,
    { revalidateOnFocus: false }
  );
};

// Available delivery dates hook (spec §9). Returns a 14-day window with
// availability flags. Pass the SUPPLIER businessId — that's whose cutoff and
// holiday calendar govern. Falls back to caller's own business when omitted.
export const useAvailableDeliveryDates = (
  supplierBusinessId: string | undefined | null,
  enabled: boolean,
) => {
  return useSWR<IAvailableDeliveryDatesResponse>(
    enabled ? `/ord/governance-config/available-delivery-dates:${supplierBusinessId ?? "self"}` : null,
    () => GovernanceApi.availableDeliveryDates(
      supplierBusinessId ? { businessId: supplierBusinessId, days: 14 } : { days: 14 }
    ),
    { revalidateOnFocus: false, dedupingInterval: 60_000 }
  );
};

// Receiver branch details hook
export const useReceiverBranch = () => {
  const [receiverBranch, setReceiverBranch] = useState<any>(null);

  const fetchBranch = async (id: string) => {
    if (id) {
      try {
        const res = await BranchApi.get(id);
        setReceiverBranch(res);
        return res;
      } catch (err) {
        message.error((err as HttpHandler)?.message!);
      }
    }
  };

  return { receiverBranch, fetchBranch };
};
